import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule } from '@angular/forms';
import { Destroyable } from '../../../utils/mixins/destroyable.mixin';
import { SimulationsService } from '../../../services/simulations/simulations.service';
import { LoadingService } from '../../../services/loading.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { SimulatorType } from '../../../core/enums/simulator-type.enum';
import { CubeSizeResult, RunSearchFilters, SimulationSearchFilters } from '../../../core/models/simulation.model';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { FilterOptionsPopupComponent } from '../filter-options-popup/filter-options-popup.component';
import { filter } from 'rxjs/operators';
import { RemoveExtensionPipe } from '../../pipes/remove-extension.pipe';
import { sanitizeSearchString } from '../../../utils/sanitize.util';

interface SimulationRow {
  simulationId: string;
  simulationName: string;
  fileName: string;
  results: Array<{
    direction: string;
    runId: string;
    cubeSizeResults: CubeSizeResult[];
  }>;
  selectedDirection: string | null;
  selectedCubeSize: CubeSizeResult | null;
  mValue: number | null; // exponentialCoefficient (m value in Archie's law)
}

@Component({
  selector: 'app-select-formation-factor-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatRadioModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    RemoveExtensionPipe,
  ],
  templateUrl: './select-formation-factor-popup.component.html',
  styleUrl: './select-formation-factor-popup.component.scss',
})
export class SelectFormationFactorPopupComponent extends Destroyable(Object) implements OnInit {
  private readonly componentId: string = 'select-formation-factor-popup';
  isLoading = false;
  simulations: SimulationRow[] = [];
  selectedSimulation: SimulationRow | null = null;

  readonly searchByOptions: { label: string; value: string }[] = [
    { label: 'Name', value: 'Name' },
    { label: 'Rock file', value: 'File' },
  ];
  filterByOptionSelected: string = 'File';
  searchString: string = '';
  filterData: RunSearchFilters | null = null;
  readonly runStatuses = ['Success', 'Failure', 'Waiting', 'Initializing', 'Processing', 'Canceled'];

  displayedColumns: string[] = [
    'select',
    'simulationName',
    'rockFile',
    'direction',
    'cubeSize',
    'mValue',
  ];

  private preselection: {
    simulationId: string;
    direction: string;
    cubeSizeVoxels?: number;
  } | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<SelectFormationFactorPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      fileName: string;
      preselection?: { simulationId: string; direction: string; cubeSizeVoxels?: number };
    },
    private readonly simulationsService: SimulationsService,
    private readonly loadingService: LoadingService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: MatDialog,
  ) {
    super();
    this.preselection = data.preselection || null;
  }

  ngOnInit(): void {
    this.subscribeToLoadingState();
    this.loadSimulations();
  }

  onSelect(): void {
    if (this.selectedSimulation && this.selectedSimulation.selectedDirection && this.selectedSimulation.selectedCubeSize) {
      this.dialogRef.close({
        simulationId: this.selectedSimulation.simulationId,
        simulationName: this.selectedSimulation.simulationName,
        direction: this.selectedSimulation.selectedDirection,
        cubeSize: this.selectedSimulation.selectedCubeSize.cubeSizeVoxels,
        cubeSizeVoxels: this.selectedSimulation.selectedCubeSize.cubeSizeVoxels,
        cubeSizeFraction: this.selectedSimulation.selectedCubeSize.cubeSizeFraction,
        angularCoefficient: this.selectedSimulation.selectedCubeSize.angularCoefficient,
        exponentialCoefficient: this.selectedSimulation.mValue!,
        mValue: this.selectedSimulation.mValue!,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  selectSimulation(simulation: SimulationRow): void {
    this.selectedSimulation = simulation;
  }

  onDirectionChange(simulation: SimulationRow): void {
    if (!simulation.selectedDirection) {
      simulation.selectedCubeSize = null;
      simulation.mValue = null;
      return;
    }
    simulation.selectedCubeSize = null;
    simulation.mValue = null;
    this.updateCoefficients(simulation);
  }

  onCubeSizeChange(simulation: SimulationRow): void {
    this.updateCoefficients(simulation);
  }

  private updateCoefficients(simulation: SimulationRow): void {
    if (!simulation.selectedDirection) {
      simulation.mValue = null;
      return;
    }

    const selectedResult = simulation.results.find(
      (r) => r.direction.toLowerCase() === simulation.selectedDirection!.toLowerCase(),
    );

    if (selectedResult && simulation.selectedCubeSize) {
      const cubeSizeResult = selectedResult.cubeSizeResults.find(
        (cs) => cs.cubeSizeVoxels === simulation.selectedCubeSize!.cubeSizeVoxels,
      );
      if (cubeSizeResult) {
        simulation.mValue = cubeSizeResult.exponentialCoefficient;
      }
    }
  }

  getAvailableDirections(simulation: SimulationRow): string[] {
    return simulation.results.map((r) => r.direction.toUpperCase());
  }

  getAvailableCubeSizes(simulation: SimulationRow): CubeSizeResult[] {
    if (!simulation.selectedDirection) {
      return [];
    }
    const selectedResult = simulation.results.find(
      (r) => r.direction.toLowerCase() === simulation.selectedDirection!.toLowerCase(),
    );
    return selectedResult?.cubeSizeResults || [];
  }

  getCubeSizeDisplay(cubeSize: CubeSizeResult): string {
    return `${cubeSize.cubeSizeVoxels} (${cubeSize.cubeSizeFraction}%)`;
  }

  compareCubeSize(a: CubeSizeResult | null, b: CubeSizeResult | null): boolean {
    if (!a || !b) return a === b;
    return a.cubeSizeVoxels === b.cubeSizeVoxels && a.cubeSizeFraction === b.cubeSizeFraction;
  }

  onSearch(): void {
    this.loadSimulations();
  }

  onClearSearch(): void {
    this.searchString = '';
    this.onSearch();
  }

  onSearchStringChange(): void {
    if (!this.searchString) {
      this.onClearSearch();
    }
  }

  openFilterPopup(): void {
    const dialogRef = this.dialog.open(FilterOptionsPopupComponent, {
      disableClose: true,
      data: {
        ...this.filterData,
        statuses: this.runStatuses,
        hideTypes: true,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        this.takeUntilDestroyed(),
      )
      .subscribe((data: SimulationSearchFilters) => {
        const directions = [];
        if (data.directions?.['x']) directions.push('x');
        if (data.directions?.['y']) directions.push('y');
        if (data.directions?.['z']) directions.push('z');

        if (this.filterData) {
          this.filterData.direction = directions;
        }
        this.loadSimulations();
      });
  }

  private loadSimulations(): void {
    this.loadingService.startLoading(this.componentId);

    const filterData: RunSearchFilters = {
      filterBy: this.filterByOptionSelected,
      searchString: sanitizeSearchString(this.searchString),
      type: SimulatorType.SubsampleCubeTrendFF,
      direction: this.filterData?.direction || [],
      cycles: this.filterData?.cycles || [],
      interfacialTensionMin: this.filterData?.interfacialTensionMin || null,
      interfacialTensionMax: this.filterData?.interfacialTensionMax || null,
      primaryDrainageMin: this.filterData?.primaryDrainageMin || null,
      primaryDrainageMax: this.filterData?.primaryDrainageMax || null,
      firstImbibitionMin: this.filterData?.firstImbibitionMin || null,
      firstImbibitionMax: this.filterData?.firstImbibitionMax || null,
      sortColumnName: this.filterData?.sortColumnName || null,
      sortDirection: this.filterData?.sortDirection || null,
      page: 1,
      pageSize: 100,
    };

    this.filterData = filterData;

    this.simulationsService
      .getFilteredSimulations(filterData, true)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (result) => {
          this.processSimulations(result.data);
          this.applyPreselection();
          this.loadingService.stopLoading(this.componentId);
        },
        error: () => {
          this.snackbarService.openCustom({
            message: 'Failed to load simulations',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
          this.loadingService.stopLoading(this.componentId);
        },
      });
  }

  private applyPreselection(): void {
    if (!this.preselection) return;

    const match = this.simulations.find(
      (s) => s.simulationId === this.preselection!.simulationId,
    );
    if (!match) return;

    this.selectedSimulation = match;
    match.selectedDirection = match.results.find(
      (r) => r.direction.toLowerCase() === this.preselection!.direction.toLowerCase(),
    )?.direction.toUpperCase() || null;

    if (match.selectedDirection && this.preselection.cubeSizeVoxels != null) {
      const cubeSizes = this.getAvailableCubeSizes(match);
      match.selectedCubeSize = cubeSizes.find(
        (cs) => cs.cubeSizeVoxels === this.preselection!.cubeSizeVoxels,
      ) || null;
      this.updateCoefficients(match);
    }

    this.preselection = null;
  }

  private processSimulations(data: any[]): void {
    this.simulations = [];

    data.forEach((simulation: any) => {
      const results: Array<{
        direction: string;
        runId: string;
        cubeSizeResults: CubeSizeResult[];
      }> = [];

      if (simulation.results && Array.isArray(simulation.results)) {
        simulation.results.forEach((result: any) => {
          if (result.result && result.result.cubeSizeResults && Array.isArray(result.result.cubeSizeResults)) {
            const validCubeSizes = result.result.cubeSizeResults.filter(
              (cs: any) =>
                cs !== null &&
                cs.angularCoefficient !== undefined &&
                cs.exponentialCoefficient !== undefined,
            );
            if (validCubeSizes.length > 0) {
              results.push({
                direction: result.direction,
                runId: result.runId,
                cubeSizeResults: validCubeSizes,
              });
            }
          }
        });
      }

      if (results.length > 0) {
        this.simulations.push({
          simulationId: simulation.id,
          simulationName: simulation.name,
          fileName: simulation.fileName,
          results,
          selectedDirection: null,
          selectedCubeSize: null,
          mValue: null,
        });
      }
    });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}